/**
 * Versioned HTTP contract — `v1` — for the per-stage REFINEMENT HISTORY
 * (`POST /v1/ideas/stage-revisions/*`, IDEA-48).
 *
 * Mirrors the spec-version endpoints (IDEA-28): append an immutable revision when a
 * stage is refined or (re)generated, list a stage's history, restore an earlier
 * revision (non-destructive — the caller re-applies the assumptions and a NEW revision
 * row records the restore). `assumptions` is a structural guard at the wire layer; the
 * engine re-parses stage-keyed at its own boundary (ADR 0005).
 */
import { z } from 'zod';
import { apiEnvelope, ModelSelectionSchema, StageKeySchema } from './v1.js';
import { OwnerScopeSchema } from './persistence-v1.js';

const id = z.string().min(1).max(64);
const label = z.string().min(1).max(120);

/** Wire shape of a persisted append-only stage revision. */
export const StageRevisionPayloadSchema = z.object({
  id,
  ideaId: id,
  ownerId: id,
  schemaVersion: z.number().int().positive(),
  stageKey: StageKeySchema,
  createdAt: z.number().int().nonnegative(),
  label,
  /** The stage's assumptions as of this revision (structural; engine re-parses). */
  assumptions: z.record(z.unknown()),
  /** Operator feedback that produced this revision; absent for plain (re)generation. */
  refinementPrompt: z.string().min(1).max(2000).optional(),
  /** The explicit model/thinking choice that ran it; absent = server default. */
  modelSelection: ModelSelectionSchema.optional(),
  /** Ties the revision to its run + cost-ledger rows (duration/cost join here). */
  correlationId: z.string().min(1).max(120).optional(),
  restoredFrom: id.optional(),
});
export type StageRevisionPayload = z.infer<typeof StageRevisionPayloadSchema>;

/** Shared response schema for a single stage revision. */
export const StageRevisionResponseSchema = apiEnvelope(StageRevisionPayloadSchema);
export type StageRevisionResponse = z.infer<typeof StageRevisionResponseSchema>;

/** Request: append an immutable stage revision (server stamps id/version/createdAt). */
export const SaveStageRevisionRequestSchema = OwnerScopeSchema.extend({
  ideaId: id,
  stageKey: StageKeySchema,
  label,
  assumptions: z.record(z.unknown()),
  refinementPrompt: z.string().min(1).max(2000).optional(),
  modelSelection: ModelSelectionSchema.optional(),
  correlationId: z.string().min(1).max(120).optional(),
  restoredFrom: id.optional(),
});
export type SaveStageRevisionRequest = z.infer<typeof SaveStageRevisionRequestSchema>;

/** Request: list one stage's revision history for an idea (newest first, bounded). */
export const ListStageRevisionsRequestSchema = OwnerScopeSchema.extend({
  ideaId: id,
  stageKey: StageKeySchema,
});
export type ListStageRevisionsRequest = z.infer<typeof ListStageRevisionsRequestSchema>;
/** Response: the stage's revisions. */
export const ListStageRevisionsResponseSchema = apiEnvelope(z.array(StageRevisionPayloadSchema));
export type ListStageRevisionsResponse = z.infer<typeof ListStageRevisionsResponseSchema>;

/** Request: fetch one revision (drives Restore — the web re-applies its assumptions). */
export const GetStageRevisionRequestSchema = OwnerScopeSchema.extend({
  ideaId: id,
  revisionId: id,
});
export type GetStageRevisionRequest = z.infer<typeof GetStageRevisionRequestSchema>;
